import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Eye, Key, FolderOpen, FileText, Save, X, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { authFetch } from '@/utils/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Tabs ────────────────────────────────────────────────────────────────────

function TabButton({ active, onClick, children, icon: Icon, testId }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
        active ? 'bg-white text-black' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
      }`}
      data-testid={testId}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

// ─── Post Editor ─────────────────────────────────────────────────────────────

function PostEditor({ post, categories, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    content: post?.content || '',
    excerpt: post?.excerpt || '',
    category_id: post?.category_id || '',
    tags: post?.tags?.join(', ') || '',
    featured_image: post?.featured_image || '',
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
    status: post?.status || 'draft',
  });
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async (status) => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      status: status || form.status,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    };
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">{post ? 'Edit Post' : 'New Post'}</h2>
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-700 transition-colors" data-testid="editor-cancel">
            Cancel
          </button>
          <button onClick={() => handleSave('draft')} disabled={saving} className="px-4 py-2 rounded-lg text-sm bg-zinc-800 text-white hover:bg-zinc-700 transition-colors disabled:opacity-50" data-testid="editor-save-draft">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Draft'}
          </button>
          <button onClick={() => handleSave('published')} disabled={saving} className="px-4 py-2 rounded-lg text-sm bg-white text-black hover:bg-zinc-200 transition-colors disabled:opacity-50 font-medium" data-testid="editor-publish">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publish'}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main editor */}
        <div className="lg:col-span-2 space-y-4">
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Post title"
            className="w-full px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white text-xl font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
            data-testid="editor-title"
          />

          {/* Preview toggle */}
          <div className="flex border-b border-zinc-800">
            <button
              onClick={() => setPreview(false)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${!preview ? 'border-white text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            >
              Write
            </button>
            <button
              onClick={() => setPreview(true)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${preview ? 'border-white text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            >
              Preview
            </button>
          </div>

          {preview ? (
            <div className="min-h-[400px] p-4 rounded-lg bg-zinc-900 border border-zinc-800 prose prose-invert prose-zinc max-w-none prose-headings:text-white prose-p:text-zinc-300 prose-a:text-white prose-code:bg-zinc-800 prose-code:rounded prose-pre:bg-zinc-900 prose-pre:border prose-pre:border-zinc-800 prose-blockquote:border-zinc-700 prose-blockquote:text-zinc-400">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {form.content || '*Nothing to preview*'}
              </ReactMarkdown>
            </div>
          ) : (
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Write your post in Markdown..."
              className="w-full min-h-[400px] px-4 py-3 rounded-lg bg-zinc-900 border border-zinc-800 text-white text-sm font-mono placeholder-zinc-600 focus:outline-none focus:border-zinc-600 resize-y"
              data-testid="editor-content"
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-800 space-y-4">
            <h3 className="text-sm font-medium text-zinc-400">Post Settings</h3>

            <div>
              <label className="text-xs text-zinc-500 block mb-1">Slug</label>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="auto-generated-from-title"
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white placeholder-zinc-600 focus:outline-none"
                data-testid="editor-slug"
              />
            </div>

            <div>
              <label className="text-xs text-zinc-500 block mb-1">Category</label>
              <select
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white focus:outline-none"
                data-testid="editor-category"
              >
                <option value="">None</option>
                {categories.map((c) => (
                  <option key={c.category_id} value={c.category_id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-zinc-500 block mb-1">Tags (comma-separated)</label>
              <input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="business, spotlight"
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white placeholder-zinc-600 focus:outline-none"
                data-testid="editor-tags"
              />
            </div>

            <div>
              <label className="text-xs text-zinc-500 block mb-1">Featured Image URL</label>
              <input
                value={form.featured_image}
                onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white placeholder-zinc-600 focus:outline-none"
                data-testid="editor-image"
              />
              {form.featured_image && (
                <img src={form.featured_image} alt="Preview" className="mt-2 rounded-lg w-full h-32 object-cover border border-zinc-700" />
              )}
            </div>

            <div>
              <label className="text-xs text-zinc-500 block mb-1">Excerpt</label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="Short summary (auto-generated if empty)"
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white placeholder-zinc-600 focus:outline-none resize-none"
                data-testid="editor-excerpt"
              />
            </div>
          </div>

          <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-800 space-y-4">
            <h3 className="text-sm font-medium text-zinc-400">SEO</h3>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Meta Title</label>
              <input
                value={form.meta_title}
                onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                placeholder="Defaults to post title"
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white placeholder-zinc-600 focus:outline-none"
                data-testid="editor-meta-title"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-500 block mb-1">Meta Description</label>
              <textarea
                value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                placeholder="Defaults to first 160 chars"
                rows={2}
                className="w-full px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 text-sm text-white placeholder-zinc-600 focus:outline-none resize-none"
                data-testid="editor-meta-desc"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Category Manager ────────────────────────────────────────────────────────

function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCats = useCallback(async () => {
    const res = await fetch(`${API}/blog/categories`);
    setCategories(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchCats(); }, [fetchCats]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const res = await authFetch(`${API}/blog/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName, description: newDesc }),
    });
    if (res.ok) {
      toast.success('Category created');
      setNewName('');
      setNewDesc('');
      fetchCats();
    } else {
      const err = await res.json();
      toast.error(err.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    const res = await authFetch(`${API}/blog/categories/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Deleted'); fetchCats(); }
    else toast.error('Failed to delete');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Blog Categories</h2>
      {/* Create */}
      <div className="flex gap-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Category name"
          className="flex-1 px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-white text-sm placeholder-zinc-600 focus:outline-none"
          data-testid="cat-name-input"
        />
        <input
          value={newDesc}
          onChange={(e) => setNewDesc(e.target.value)}
          placeholder="Description (optional)"
          className="flex-1 px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-white text-sm placeholder-zinc-600 focus:outline-none"
          data-testid="cat-desc-input"
        />
        <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors" data-testid="cat-create-btn">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {/* List */}
      {loading ? (
        <div className="animate-pulse space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-12 bg-zinc-800 rounded-lg" />)}
        </div>
      ) : categories.length === 0 ? (
        <p className="text-zinc-500 text-sm">No categories yet.</p>
      ) : (
        <div className="space-y-2">
          {categories.map((c) => (
            <div key={c.category_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900 border border-zinc-800">
              <div>
                <span className="text-white text-sm font-medium">{c.name}</span>
                <span className="text-zinc-600 text-xs ml-2">/{c.slug}</span>
                {c.description && <p className="text-zinc-500 text-xs mt-0.5">{c.description}</p>}
              </div>
              <button onClick={() => handleDelete(c.category_id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`cat-delete-${c.slug}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── API Key Manager ─────────────────────────────────────────────────────────

function ApiKeyManager() {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchKeys = useCallback(async () => {
    const res = await authFetch(`${API}/blog/api-keys`);
    if (res.ok) setKeys(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const res = await authFetch(`${API}/blog/api-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
    });
    if (res.ok) {
      const data = await res.json();
      setNewKey(data.key);
      setNewName('');
      fetchKeys();
      toast.success('API key created — copy it now, it won\'t be shown again');
    } else {
      toast.error('Failed to create');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revoke this API key?')) return;
    const res = await authFetch(`${API}/blog/api-keys/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Revoked'); fetchKeys(); }
  };

  const copyKey = async () => {
    if (newKey) {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">API Keys</h2>
      <p className="text-zinc-500 text-sm">Create API keys for AI agents (Wingman) to publish blog posts programmatically.</p>

      {/* New key alert */}
      {newKey && (
        <div className="p-4 rounded-lg bg-green-900/30 border border-green-800">
          <p className="text-sm text-green-300 mb-2 font-medium">New API key created — copy it now!</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-zinc-900 p-2 rounded text-green-200 overflow-x-auto">{newKey}</code>
            <button onClick={copyKey} className="p-2 rounded bg-zinc-800 text-green-300 hover:bg-zinc-700" data-testid="copy-new-key">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="mt-2 text-xs text-zinc-500 hover:text-white">Dismiss</button>
        </div>
      )}

      {/* Create */}
      <div className="flex gap-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Key name (e.g. Wingman Bot)"
          className="flex-1 px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-white text-sm placeholder-zinc-600 focus:outline-none"
          data-testid="apikey-name-input"
        />
        <button onClick={handleCreate} className="px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors" data-testid="apikey-create-btn">
          Create Key
        </button>
      </div>

      {/* Usage instructions */}
      <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-800">
        <h4 className="text-sm font-medium text-zinc-300 mb-2">Usage</h4>
        <pre className="text-xs text-zinc-400 overflow-x-auto whitespace-pre-wrap">{`curl -X POST ${window.location.origin}/api/blog/posts \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_KEY" \\
  -d '{"title":"My Post","content":"# Hello World\\n...","status":"published","category_id":"..."}'`}</pre>
      </div>

      {/* List */}
      {loading ? (
        <div className="animate-pulse space-y-2">
          {[1,2].map(i => <div key={i} className="h-12 bg-zinc-800 rounded-lg" />)}
        </div>
      ) : keys.length === 0 ? (
        <p className="text-zinc-500 text-sm">No API keys yet.</p>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <div key={k.key_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900 border border-zinc-800">
              <div>
                <span className="text-white text-sm font-medium">{k.name}</span>
                <code className="text-zinc-600 text-xs ml-2">{k.key}</code>
                {k.revoked && <span className="ml-2 text-xs text-red-400">Revoked</span>}
                {k.last_used_at && <span className="ml-2 text-xs text-zinc-600">Last used: {new Date(k.last_used_at).toLocaleDateString()}</span>}
              </div>
              {!k.revoked && (
                <button onClick={() => handleRevoke(k.key_id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`apikey-revoke-${k.key_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AdminBlogPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null); // null = list, 'new' = create, object = edit

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const res = await authFetch(`${API}/blog/posts?status=draft&limit=50`);
    const pub = await authFetch(`${API}/blog/posts?status=published&limit=50`);
    const drafts = res.ok ? (await res.json()).posts : [];
    const published = pub.ok ? (await pub.json()).posts : [];
    setPosts([...drafts, ...published].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)));
    setLoading(false);
  }, []);

  const fetchCategories = useCallback(async () => {
    const res = await fetch(`${API}/blog/categories`);
    setCategories(await res.json());
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [fetchPosts, fetchCategories]);

  const handleEditPost = async (post) => {
    // Fetch full post content (list view excludes it for performance)
    try {
      const res = await fetch(`${API}/blog/posts/${post.slug}`);
      if (res.ok) {
        const fullPost = await res.json();
        setEditingPost(fullPost);
      } else {
        setEditingPost(post); // fallback to list data
      }
    } catch {
      setEditingPost(post);
    }
  };

  const handleSavePost = async (payload) => {
    const isEdit = editingPost && editingPost !== 'new';
    const url = isEdit ? `${API}/blog/posts/${editingPost.slug}` : `${API}/blog/posts`;
    const method = isEdit ? 'PUT' : 'POST';

    const res = await authFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      toast.success(isEdit ? 'Post updated' : 'Post created');
      setEditingPost(null);
      fetchPosts();
    } else {
      const err = await res.json();
      toast.error(err.detail || 'Failed to save');
    }
  };

  const handleDeletePost = async (slug) => {
    if (!window.confirm('Delete this post?')) return;
    const res = await authFetch(`${API}/blog/posts/${slug}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Deleted'); fetchPosts(); }
    else toast.error('Failed to delete');
  };

  // Editor view
  if (editingPost) {
    return (
      <div className="min-h-screen bg-[#050505] pt-6 px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <PostEditor
            post={editingPost !== 'new' ? editingPost : null}
            categories={categories}
            onSave={handleSavePost}
            onCancel={() => setEditingPost(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-6 px-6 pb-20">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-500 hover:text-white transition-colors" data-testid="blog-admin-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-white">Blog Manager</h1>
          </div>
          {tab === 'posts' && (
            <button
              onClick={() => setEditingPost('new')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors"
              data-testid="blog-new-post"
            >
              <Plus className="w-4 h-4" /> New Post
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-zinc-800 pb-3">
          <TabButton active={tab === 'posts'} onClick={() => setTab('posts')} icon={FileText} testId="blog-tab-posts">Posts</TabButton>
          <TabButton active={tab === 'categories'} onClick={() => setTab('categories')} icon={FolderOpen} testId="blog-tab-categories">Categories</TabButton>
          <TabButton active={tab === 'apikeys'} onClick={() => setTab('apikeys')} icon={Key} testId="blog-tab-apikeys">API Keys</TabButton>
        </div>

        {/* Content */}
        {tab === 'posts' && (
          <div className="space-y-2">
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[1,2,3,4].map(i => <div key={i} className="h-16 bg-zinc-800 rounded-lg" />)}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16">
                <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No posts yet. Create your first post!</p>
              </div>
            ) : (
              posts.map((p) => (
                <div key={p.post_id} className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white text-sm font-medium truncate">{p.title}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        p.status === 'published' ? 'bg-green-900/40 text-green-400' :
                        p.status === 'draft' ? 'bg-yellow-900/40 text-yellow-400' :
                        'bg-zinc-800 text-zinc-500'
                      }`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-600">
                      {p.category_name && <span>{p.category_name}</span>}
                      <span>{p.reading_time} min read</span>
                      <span>{p.views_count || 0} views</span>
                      <span>{new Date(p.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    {p.status === 'published' && (
                      <Link to={`/blog/${p.slug}`} className="p-2 text-zinc-500 hover:text-white transition-colors" data-testid={`post-view-${p.slug}`}>
                        <Eye className="w-4 h-4" />
                      </Link>
                    )}
                    <button onClick={() => handleEditPost(p)} className="p-2 text-zinc-500 hover:text-white transition-colors" data-testid={`post-edit-${p.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeletePost(p.slug)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`post-delete-${p.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'categories' && <CategoryManager />}
        {tab === 'apikeys' && <ApiKeyManager />}
      </div>
    </div>
  );
}
